/**
 * Inputs: trip + a day plan + owner cost/payment answers.
 * Outputs: the same trip with a linked booking, and a supplier payment only when
 * the owner said the supplier has already been paid.
 * Never invents a 50/50 split for a one-household cost. Never records a payment
 * without a cost. Existing payments are left untouched.
 */

import type { Allocation, Booking, SupplierPayment, TripDocument, TripEvent } from "./schema.ts";
import { createId } from "./ids.ts";
import { money } from "./money.ts";
import { bookingHasFinancialHistory } from "./financial.ts";
import { previewPayment } from "./ledger.ts";
import { fundingPreviewText } from "./funding.ts";
import { formatMinor } from "./money.ts";
import { bookingStatusAfterPlanChange } from "./plan-status.ts";

export type PlanCostShare = "split" | "chris" | "paul";
export type SupplierPaid = "unpaid" | "paid" | "unknown";

export type PlanBookingDraft = {
  provider: string;
  confirmation: string;
  amount: string;
  currency: "THB" | "GBP";
  share: PlanCostShare;
  paid: SupplierPaid;
  payerHouseholdId: "chris" | "paul";
  paidDate: string;
};

export const EMPTY_PLAN_BOOKING: PlanBookingDraft = {
  provider: "",
  confirmation: "",
  amount: "",
  currency: "THB",
  share: "split",
  paid: "unpaid",
  payerHouseholdId: "chris",
  paidDate: "",
};

export function parseMajorAmount(raw: string): { amountMinor: number } | { empty: true } | { error: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { empty: true };
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return { error: "Enter a valid amount, for example 7500." };
  const amountMinor = Math.round(parsed * 100);
  if (!Number.isInteger(amountMinor) || amountMinor <= 0) return { error: "Enter a valid amount, for example 7500." };
  return { amountMinor };
}

export function allocationsForShare(amountMinor: number, share: PlanCostShare): Allocation[] {
  if (share === "chris") return [{ householdId: "chris", amountMinor }];
  if (share === "paul") return [{ householdId: "paul", amountMinor }];
  const chris = Math.trunc(amountMinor / 2);
  return [
    { householdId: "chris", amountMinor: chris },
    { householdId: "paul", amountMinor: amountMinor - chris },
  ];
}

export function bookingKindForEvent(kind: TripEvent["kind"]): Booking["kind"] | null {
  if (kind === "excursion") return "excursion";
  if (kind === "transfer") return "transfer_package";
  return null;
}

export function draftFromBooking(booking: Booking | undefined, paidDate: string): PlanBookingDraft {
  if (!booking) return { ...EMPTY_PLAN_BOOKING, paidDate };
  const chris = booking.allocations.find((row) => row.householdId === "chris");
  const paul = booking.allocations.find((row) => row.householdId === "paul");
  const share: PlanCostShare = chris && paul ? "split" : paul && !chris ? "paul" : "chris";
  return {
    provider: booking.provider ?? "",
    confirmation: booking.confirmation ?? "",
    amount: booking.cost ? String(booking.cost.amountMinor / 100) : "",
    currency: booking.cost?.currency === "GBP" ? "GBP" : "THB",
    share,
    paid: "unpaid",
    payerHouseholdId: "chris",
    paidDate,
  };
}

export function planPaymentPreview(
  doc: TripDocument,
  draft: PlanBookingDraft,
): string | null {
  const parsed = parseMajorAmount(draft.amount);
  if (!("amountMinor" in parsed) || draft.paid !== "paid") return null;
  const allocations = allocationsForShare(parsed.amountMinor, draft.share);
  const booking: Booking = {
    id: "preview",
    kind: "excursion",
    title: "Preview",
    bookingStatus: "booked_owner_reported",
    cost: money(draft.currency, parsed.amountMinor),
    allocations,
    notes: [],
    sourceIds: [],
    attachmentIds: [],
  };
  const funded = allocations;
  const preview = previewPayment(booking, [], [], doc.households.map((h) => h.id), {
    payerHouseholdId: draft.payerHouseholdId,
    credit: booking.cost!,
    fundedAllocations: funded,
  });
  const labels = Object.fromEntries(doc.households.map((h) => [h.id, h.label]));
  return fundingPreviewText({
    payerLabel: labels[draft.payerHouseholdId] ?? draft.payerHouseholdId,
    householdLabels: labels,
    funded,
    claims: preview.claims,
    format: formatMinor,
    currency: draft.currency,
  });
}

export function applyPlanBooking(
  doc: TripDocument,
  event: TripEvent,
  draft: PlanBookingDraft,
  nowIso: string,
): { doc: TripDocument } | { error: string } {
  const kind = bookingKindForEvent(event.kind);
  if (!kind) return { error: "This plan type does not take a supplier booking." };

  const parsed = parseMajorAmount(draft.amount);
  if ("error" in parsed) return { error: parsed.error };
  if (draft.paid === "paid" && "empty" in parsed) {
    return { error: "To record that it is paid, enter what was paid to the supplier." };
  }

  const existing = event.bookingId
    ? doc.bookings.find((row) => row.id === event.bookingId)
    : undefined;
  const amountMinor = "amountMinor" in parsed ? parsed.amountMinor : null;
  const hasHistory = existing ? bookingHasFinancialHistory(doc, existing.id) : false;

  if (existing && hasHistory && amountMinor != null && existing.cost
    && (existing.cost.amountMinor !== amountMinor || existing.cost.currency !== draft.currency)) {
    return { error: "Cost is locked after a payment or refund is recorded. Change it from Money." };
  }

  const allocations = amountMinor != null ? allocationsForShare(amountMinor, draft.share) : existing?.allocations ?? [];
  const bookingId = existing?.id ?? createId("booking");
  const nextBooking: Booking = {
    ...(existing ?? {
      id: bookingId,
      kind,
      stayId: null,
      notes: kind === "transfer_package"
        ? ["Package cost counted once if both legs share this booking."]
        : [],
      sourceIds: [],
      attachmentIds: [],
    }),
    id: bookingId,
    kind,
    title: event.title ?? existing?.title,
    provider: draft.provider.trim() || existing?.provider || null,
    confirmation: draft.confirmation.trim() || existing?.confirmation || null,
    startDate: event.date,
    endDate: event.date,
    householdId: allocations.length === 1 ? allocations[0]?.householdId ?? null : null,
    bookingStatus: bookingStatusAfterPlanChange(
      existing?.bookingStatus ?? "planned",
      event.planStatus === "cancelled" || event.planStatus === "booked" || event.planStatus === "idea"
        ? event.planStatus
        : "planned",
    ),
    cost: amountMinor != null
      ? money(draft.currency, amountMinor)
      : existing?.cost ?? null,
    allocations: amountMinor != null ? allocations : existing?.allocations ?? [],
    allocationStatus: allocations.length === 1 ? "assigned" : (allocations.length > 1 ? "split_to_confirm" : existing?.allocationStatus),
    paymentBaseline: draft.paid === "unpaid" && amountMinor != null && !hasHistory
      ? { status: "unpaid_owner_reported", asOf: draft.paidDate || nowIso.slice(0, 10) }
      : existing?.paymentBaseline,
  };

  const bookings = existing
    ? doc.bookings.map((row) => (row.id === existing.id ? nextBooking : row))
    : [...doc.bookings, nextBooking];
  const events = doc.events.map((row) => (
    row.id === event.id
      ? { ...row, bookingId, planStatus: event.planStatus ?? row.planStatus }
      : row
  ));

  if (draft.paid !== "paid" || !nextBooking.cost) {
    return { doc: { ...doc, bookings, events } };
  }

  const alreadyPaid = doc.supplierPayments.some((row) => row.bookingId === bookingId);
  if (alreadyPaid) return { doc: { ...doc, bookings, events } };

  const payment: SupplierPayment = {
    id: createId("pay"),
    bookingId,
    payerHouseholdId: draft.payerHouseholdId,
    credit: nextBooking.cost,
    date: draft.paidDate || nowIso.slice(0, 10),
    fundedAllocations: nextBooking.allocations,
    note: "Recorded when the plan was saved as booked and paid.",
    idempotencyKey: createId("idem"),
    createdAt: nowIso,
    createdBy: "owner session",
  };
  return {
    doc: {
      ...doc,
      bookings,
      events,
      supplierPayments: [...doc.supplierPayments, payment],
    },
  };
}
